import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaGithub,
  FaLinkedinIn,
  FaCode,
  FaPaperPlane,
} from 'react-icons/fa';
import './Contact.css';

const Contact = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <section id="contact" className="py-28 glow-contact">
      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="container mx-auto px-6 max-w-5xl"
      >
        <motion.h2
          variants={itemVariants}
          className="text-5xl md:text-6xl font-bold font-poppins tracking-tight text-white mb-4"
        >
          Get In <span className="gradient-text">Touch</span>
        </motion.h2>
        <motion.div
          variants={itemVariants}
          className="w-24 h-px bg-gradient-to-r from-primary-400 to-transparent mb-16"
        />

        <motion.div variants={itemVariants} className="glass rounded-3xl p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div variants={itemVariants}>
              <h3 className="text-2xl font-semibold font-poppins tracking-tight mb-6 text-white">
                Let's work together!
              </h3>
              <p className="text-gray-400 mb-8 leading-relaxed">
                I'm always interested in hearing about new projects, research opportunities, and
                collaborations. Whether you have a question or just want to connect, feel free to
                reach out!
              </p>

              <div className="space-y-6">
                {/* Email */}
                <motion.div
                  whileHover={{ x: 10 }}
                  className="flex items-center space-x-4"
                >
                  <div className="bg-white/5 border border-white/10 p-4 rounded-2xl">
                    <FaEnvelope className="text-primary-300 text-xl" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">Email</p>
                    <a
                      href="mailto:manish03.svt@gmail.com"
                      className="text-gray-400 hover:text-secondary-300 transition-colors"
                    >
                      manish03.svt@gmail.com
                    </a>
                  </div>
                </motion.div>

                {/* Phone */}
                <motion.div
                  whileHover={{ x: 10 }}
                  className="flex items-center space-x-4"
                >
                  <div className="bg-white/5 border border-white/10 p-4 rounded-2xl">
                    <FaPhoneAlt className="text-primary-300 text-xl" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">Phone</p>
                    <a
                      href="tel:+919380214768"
                      className="text-gray-400 hover:text-secondary-300 transition-colors"
                    >
                      +91 93802 14768
                    </a>
                  </div>
                </motion.div>

                {/* Location */}
                <motion.div
                  whileHover={{ x: 10 }}
                  className="flex items-center space-x-4"
                >
                  <div className="bg-white/5 border border-white/10 p-4 rounded-2xl">
                    <FaMapMarkerAlt className="text-primary-300 text-xl" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">Location</p>
                    <p className="text-gray-400">Bangalore, India</p>
                  </div>
                </motion.div>
              </div>

              {/* Social Media Icons */}
              <div className="mt-8">
                <p className="font-semibold mb-4 text-white">Connect With Me</p>
                <div className="flex space-x-4">
                  <motion.a
                    whileHover={{ scale: 1.2, rotate: 5 }}
                    whileTap={{ scale: 0.9 }}
                    href="https://github.com/manishtandurkar"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/5 border border-white/10 hover:bg-primary-600 hover:border-primary-500 text-primary-300 hover:text-white p-3 rounded-full transition-all duration-300"
                  >
                    <FaGithub className="text-xl" />
                  </motion.a>
                  <motion.a
                    whileHover={{ scale: 1.2, rotate: 5 }}
                    whileTap={{ scale: 0.9 }}
                    href="https://www.linkedin.com/in/manish-tandurkar/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/5 border border-white/10 hover:bg-primary-600 hover:border-primary-500 text-primary-300 hover:text-white p-3 rounded-full transition-all duration-300"
                  >
                    <FaLinkedinIn className="text-xl" />
                  </motion.a>
                  <motion.a
                    whileHover={{ scale: 1.2, rotate: 5 }}
                    whileTap={{ scale: 0.9 }}
                    href="mailto:manish03.svt@gmail.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/5 border border-white/10 hover:bg-primary-600 hover:border-primary-500 text-primary-300 hover:text-white p-3 rounded-full transition-all duration-300"
                  >
                    <FaEnvelope className="text-xl" />
                  </motion.a>
                  <motion.a
                    whileHover={{ scale: 1.2, rotate: 5 }}
                    whileTap={{ scale: 0.9 }}
                    href="https://leetcode.com/u/manish-tandurkar/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/5 border border-white/10 hover:bg-primary-600 hover:border-primary-500 text-primary-300 hover:text-white p-3 rounded-full transition-all duration-300"
                  >
                    <FaCode className="text-xl" />
                  </motion.a>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div variants={itemVariants}>
              <form
                action="https://formspree.io/f/mblzzwvq"
                method="POST"
                className="space-y-8"
              >
                <div>
                  <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-widest mb-2 text-gray-500">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-0 py-3 bg-transparent border-0 border-b border-white/15 rounded-none text-white placeholder-gray-500 focus:outline-none focus:ring-0 focus:border-primary-400 transition-colors duration-300"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-widest mb-2 text-gray-500">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-0 py-3 bg-transparent border-0 border-b border-white/15 rounded-none text-white placeholder-gray-500 focus:outline-none focus:ring-0 focus:border-primary-400 transition-colors duration-300"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-xs font-semibold uppercase tracking-widest mb-2 text-gray-500">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-0 py-3 bg-transparent border-0 border-b border-white/15 rounded-none text-white placeholder-gray-500 focus:outline-none focus:ring-0 focus:border-primary-400 transition-colors duration-300"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-semibold uppercase tracking-widest mb-2 text-gray-500">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="w-full px-0 py-3 bg-transparent border-0 border-b border-white/15 rounded-none text-white placeholder-gray-500 focus:outline-none focus:ring-0 focus:border-primary-400 transition-colors duration-300 resize-none"
                  ></textarea>
                </div>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="submit"
                  className="w-full bg-white hover:bg-gray-200 text-night py-4 rounded-full font-semibold transition-all duration-300 shadow-neon flex items-center justify-center"
                >
                  <FaPaperPlane className="mr-2" />
                  Send Message
                </motion.button>
              </form>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Contact;
